import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import type { NormalizedApiError } from '@/shared/api';
import { EmptyState } from '@/shared/ui';

import {
  useAddProductFamilyAttribute,
  useDeleteProductFamilyAttribute,
  useUpdateProductFamilyAttribute,
} from '../../hooks';
import { resolveLocalizedText } from '../../lib/localizedText';
import type { ToastSeverity } from '../../lib/useToast';
import type { AttributeDefinition, ProductFamilyAttribute } from '../../model/types';

interface ProductFamilyAttributesEditorProps {
  familyCode: string;
  attached: ProductFamilyAttribute[];
  dictionary: AttributeDefinition[];
  canManage: boolean;
  onNotify: (severity: ToastSeverity, message: string) => void;
}

/**
 * Attribute attach/detach editor for a product family (attribute template) —
 * user-story-catalog-005 AC2/AC3, variant axis marking AC8 (user-story-catalog-008). Every
 * control commits immediately (there is no bulk endpoint: add/update/remove are each their own
 * REST call), matching the family's actual attribute-membership contract.
 */
export function ProductFamilyAttributesEditor({
  familyCode,
  attached,
  dictionary,
  canManage,
  onNotify,
}: ProductFamilyAttributesEditorProps) {
  const { t, i18n } = useTranslation('catalog');
  const addAttribute = useAddProductFamilyAttribute();
  const updateAttribute = useUpdateProductFamilyAttribute();
  const removeAttribute = useDeleteProductFamilyAttribute();

  const [pendingAttribute, setPendingAttribute] = useState<AttributeDefinition | null>(null);
  const [sortOrderDrafts, setSortOrderDrafts] = useState<Record<string, string>>({});

  const dictionaryByCode = useMemo(
    () => new Map(dictionary.map((attribute) => [attribute.code, attribute])),
    [dictionary],
  );
  const attachableOptions = useMemo(
    () => dictionary.filter((a) => !attached.some((row) => row.attributeCode === a.code)),
    [dictionary, attached],
  );
  const sortedAttached = useMemo(
    () => [...attached].sort((a, b) => a.sortOrder - b.sortOrder),
    [attached],
  );

  const labelFor = (attributeCode: string): string => {
    const attribute = dictionaryByCode.get(attributeCode);
    return attribute
      ? resolveLocalizedText(attribute.translations, 'label', i18n.language, attribute.code)
      : attributeCode;
  };

  const handleAdd = () => {
    if (!pendingAttribute) return;
    addAttribute.mutate(
      {
        code: familyCode,
        body: { attributeCode: pendingAttribute.code, required: false, sortOrder: attached.length },
      },
      {
        onSuccess: () => {
          onNotify('success', t('productFamilyAttributesEditor.added', { label: labelFor(pendingAttribute.code) }));
          setPendingAttribute(null);
        },
        onError: (error: NormalizedApiError) => {
          if (error.code === 'FAMILY_ATTRIBUTE_LIMIT_EXCEEDED') {
            onNotify('error', t('productFamilyAttributesEditor.errors.limitExceeded'));
            return;
          }
          onNotify('error', error.message);
        },
      },
    );
  };

  const commitRequired = (attributeCode: string, required: boolean) => {
    updateAttribute.mutate(
      { code: familyCode, attributeCode, body: { required } },
      { onError: (error: NormalizedApiError) => onNotify('error', error.message) },
    );
  };

  const commitVariantAxis = (attributeCode: string, variantAxis: boolean) => {
    updateAttribute.mutate(
      { code: familyCode, attributeCode, body: { variantAxis } },
      { onError: (error: NormalizedApiError) => onNotify('error', error.message) },
    );
  };

  const commitSortOrder = (attributeCode: string, currentSortOrder: number) => {
    const draft = sortOrderDrafts[attributeCode];
    if (draft === undefined) return;
    const parsed = Number(draft);
    if (Number.isNaN(parsed) || parsed === currentSortOrder) return;
    updateAttribute.mutate(
      { code: familyCode, attributeCode, body: { sortOrder: parsed } },
      { onError: (error: NormalizedApiError) => onNotify('error', error.message) },
    );
  };

  const handleRemove = (attributeCode: string) => {
    removeAttribute.mutate(
      { code: familyCode, attributeCode },
      {
        onSuccess: () =>
          onNotify('success', t('productFamilyAttributesEditor.removed', { label: labelFor(attributeCode) })),
        onError: (error: NormalizedApiError) => {
          if (error.status === 409) {
            onNotify('error', t('productFamilyAttributesEditor.errors.removeInUse'));
            return;
          }
          onNotify('error', error.message);
        },
      },
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {t('productFamilyAttributesEditor.title')}
      </Typography>

      {canManage ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Autocomplete
            options={attachableOptions}
            value={pendingAttribute}
            onChange={(_e, value) => setPendingAttribute(value)}
            getOptionLabel={(option) =>
              `${resolveLocalizedText(option.translations, 'label', i18n.language, option.code)} (${option.code})`
            }
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
              <TextField {...params} label={t('productFamilyAttributesEditor.attachLabel')} size="small" />
            )}
            sx={{ flex: 1 }}
            size="small"
          />
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon />}
            onClick={handleAdd}
            disabled={!pendingAttribute || addAttribute.isPending}
          >
            {t('productFamilyAttributesEditor.attachButton')}
          </Button>
        </Stack>
      ) : null}

      {sortedAttached.length === 0 ? (
        <EmptyState
          title={t('productFamilyAttributesEditor.empty.title')}
          description={t('productFamilyAttributesEditor.empty.description')}
        />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('productFamilyAttributesEditor.table.attribute')}</TableCell>
              <TableCell align="center">{t('productFamilyAttributesEditor.table.required')}</TableCell>
              <TableCell align="center">{t('productFamilyAttributesEditor.table.variantAxis')}</TableCell>
              <TableCell align="right">{t('productFamilyAttributesEditor.table.sortOrder')}</TableCell>
              {canManage ? <TableCell align="right" /> : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedAttached.map((row) => (
              <TableRow key={row.attributeCode} hover>
                <TableCell>
                  <Typography variant="body2">{labelFor(row.attributeCode)}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {row.attributeCode}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={row.required}
                    disabled={!canManage}
                    onChange={(e) => commitRequired(row.attributeCode, e.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': t('productFamilyAttributesEditor.requiredAria', {
                          label: labelFor(row.attributeCode),
                        }),
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={row.variantAxis}
                    disabled={!canManage}
                    onChange={(e) => commitVariantAxis(row.attributeCode, e.target.checked)}
                    slotProps={{
                      input: {
                        'aria-label': t('productFamilyAttributesEditor.variantAxisAria', {
                          label: labelFor(row.attributeCode),
                        }),
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={sortOrderDrafts[row.attributeCode] ?? String(row.sortOrder)}
                    onChange={(e) =>
                      setSortOrderDrafts((current) => ({ ...current, [row.attributeCode]: e.target.value }))
                    }
                    onBlur={() => commitSortOrder(row.attributeCode, row.sortOrder)}
                    disabled={!canManage}
                    sx={{ width: 88 }}
                    slotProps={{ htmlInput: { step: 1, 'aria-label': t('productFamilyAttributesEditor.table.sortOrder') } }}
                  />
                </TableCell>
                {canManage ? (
                  <TableCell align="right">
                    <Tooltip title={t('common:action.remove')}>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('productFamilyAttributesEditor.removeAria', {
                          label: labelFor(row.attributeCode),
                        })}
                        onClick={() => handleRemove(row.attributeCode)}
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
